import type { MenuErrorOccurrence } from 'api/menuErrors'

// Items dedupe on name + uuid so a true repeat (same item via two wire
// occurrences) collapses, while distinct items that happen to share a
// blank or sentinel uuid — should the BE ever emit one — are not
// silently dropped after the first.
const itemKey = (item: { name: string; uuid: string }) =>
  `${item.name} ${item.uuid}`

// Collapses same-reason occurrences (same severity + prefix-stripped
// description) into a single row with their items merged, so thirty
// per-item "missing image" wire messages render as one "missing image"
// row listing every affected item — the accordion shape from Figma
// 232:2019. Only parsed occurrences group (the BE emits `description`
// exactly when it could lift item attribution); unparsed raw-message
// occurrences stay as individual rows because the message is the only
// identity they carry. Location ids union, so the merged row's location
// footprint stays truthful. Input order is preserved (first appearance
// anchors the merged row), which keeps the errors-before-notices
// ordering from the caller intact.
const groupOccurrences = (
  occurrences: MenuErrorOccurrence[]
): MenuErrorOccurrence[] => {
  const grouped: MenuErrorOccurrence[] = []
  const mergedByReason = new Map<string, MenuErrorOccurrence>()
  // Seen-item keys per merged row, built once at anchor creation and
  // grown incrementally — rebuilding a Set from merged.items on every
  // merge made collapsing m occurrences into one row O(m²).
  const seenItemKeysByReason = new Map<string, Set<string>>()

  occurrences.forEach(occurrence => {
    const { severity, description } = occurrence
    if (!description) {
      grouped.push(occurrence)
      return
    }

    const reasonKey = `${severity}:${description}`
    const merged = mergedByReason.get(reasonKey)
    if (!merged) {
      const anchor = { ...occurrence, items: [...(occurrence.items ?? [])] }
      mergedByReason.set(reasonKey, anchor)
      seenItemKeysByReason.set(reasonKey, new Set(anchor.items?.map(itemKey)))
      grouped.push(anchor)
      return
    }

    const seenItemKeys = seenItemKeysByReason.get(reasonKey) as Set<string>
    const newItems = (occurrence.items ?? []).filter(
      item => !seenItemKeys.has(itemKey(item))
    )
    newItems.forEach(item => seenItemKeys.add(itemKey(item)))
    merged.items = [...(merged.items ?? []), ...newItems]

    const unionedLocationIds = new Set([
      ...merged.locationIds,
      ...occurrence.locationIds,
    ])
    merged.locationIds = Array.from(unionedLocationIds)
    merged.locationCount = merged.locationIds.length
  })

  return grouped
}

export default groupOccurrences
